"use client";
import {
  ChevronRight,
  FileCode2,
  Network,
  Search,
} from "lucide-react";
import {
  useState,
} from "react";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  StatusDot,
} from "../../shared/primitives";
import {
  AttackTechniqueChips,
  attackTechniqueSearchText,
} from "./AttackTechniqueChips";


export function DataFlowView({
  report,
  onOpenEntry,
}: {
  report: ScanReport;
  onOpenEntry: (path: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [reachableOnly, setReachableOnly] = useState(true);
  const [selectedId, setSelectedId] = useState(
    report.dataFlows[0]?.id ?? "",
  );
  const flows = report.dataFlows.filter(
    (flow) =>
      (!reachableOnly || flow.reachable) &&
      (!query ||
        `${flow.title} ${flow.sourceLabel} ${flow.sinkLabel} ${flow.entry} ${flow.method} ${flow.recoveredValues.join(" ")} ${flow.dataTypes.join(" ")} ${flow.destinations.join(" ")} ${attackTechniqueSearchText(flow.techniques)}`
          .toLowerCase()
          .includes(query.toLowerCase())),
  );
  const selected =
    report.dataFlows.find((flow) => flow.id === selectedId) ??
    flows[0] ??
    null;
  const critical = report.dataFlows.filter(
    (flow) => flow.severity === "critical",
  ).length;
  const reachable = report.dataFlows.filter((flow) => flow.reachable).length;
  const resolvedTargets = new Set(
    report.dataFlows.flatMap((flow) => flow.destinations),
  ).size;

  return (
    <div className="flow-workspace">
      <section className="flow-metrics">
        <article>
          <span>TRACED PATHS</span>
          <strong>{report.dataFlows.length}</strong>
          <small>bounded JVM evaluation</small>
        </article>
        <article className={critical ? "tone-critical" : ""}>
          <span>CRITICAL</span>
          <strong>{critical}</strong>
          <small>high-impact source to sink</small>
        </article>
        <article>
          <span>REACHABLE</span>
          <strong>{reachable}</strong>
          <small>from a supported entry point</small>
        </article>
        <article>
          <span>RESOLVED TARGETS</span>
          <strong>{resolvedTargets}</strong>
          <small>domains, URLs, IPs and webhooks</small>
        </article>
      </section>
      <section className="flow-layout panel">
        <aside className="flow-list-pane">
          <div className="flow-toolbar">
            <div className="search-field compact">
              <Search size={14} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search sources, sinks, or values"
                aria-label="Search data flows"
              />
            </div>
            <label className="check-control">
              <input
                type="checkbox"
                checked={reachableOnly}
                onChange={(event) => setReachableOnly(event.target.checked)}
              />
              <span />
              Reachable only
            </label>
          </div>
          <div className="flow-list-caption">
            {flows.length} path{flows.length === 1 ? "" : "s"}
          </div>
          <div className="flow-list">
            {flows.map((flow) => (
              <button
                type="button"
                key={flow.id}
                className={selected?.id === flow.id ? "is-selected" : ""}
                onClick={() => setSelectedId(flow.id)}
              >
                <span className={`flow-severity tone-${flow.severity}`} />
                <span>
                  <strong>{flow.title}</strong>
                  <small>
                    {flow.sourceKind} → {flow.sinkKind}
                  </small>
                  {flow.destinations[0] && (
                    <small className="flow-destination-preview">
                      {flow.destinations[0]}
                    </small>
                  )}
                  <code>
                    {flow.entry} · {flow.method}
                  </code>
                </span>
                {flow.reachable && <i>reachable</i>}
              </button>
            ))}
            {!flows.length && (
              <div className="empty-panel">
                <Network size={25} />
                <strong>No matching data paths</strong>
                <span>Include unproven reachability or clear the search.</span>
              </div>
            )}
          </div>
        </aside>
        <div className="flow-detail-pane">
          {selected ? (
            <>
              <header>
                <div className={`detail-emblem tone-${selected.severity}`}>
                  <Network size={21} />
                </div>
                <div>
                  <span className={`severity-label tone-${selected.severity}`}>
                    {selected.severity}
                  </span>
                  <h2>{selected.title}</h2>
                  <p>{selected.description}</p>
                </div>
                <button
                  className="secondary-button compact"
                  type="button"
                  onClick={() => onOpenEntry(selected.entry)}
                >
                  <FileCode2 size={14} />
                  Open class
                </button>
              </header>
              <div className="flow-endpoints">
                <div>
                  <span>SOURCE</span>
                  <strong>{selected.sourceLabel}</strong>
                  <code>{selected.sourceKind}</code>
                </div>
                <ChevronRight size={20} />
                <div>
                  <span>SINK</span>
                  <strong>{selected.sinkLabel}</strong>
                  <code>{selected.sinkKind}</code>
                </div>
              </div>
              <div className="flow-reachability">
                <StatusDot
                  severity={selected.reachable ? "safe" : "medium"}
                />
                <span>
                  <strong>
                    {selected.reachable
                      ? "Entry-point reachable"
                      : "Reachability not proven"}
                  </strong>
                  <small>
                    {selected.entryPoint ??
                      "The method was not reached from a supported manifest or loader entry point."}
                  </small>
                </span>
                <i className={`flow-order tone-${selected.ordering}`}>
                  {selected.ordering === "proven"
                    ? "ordered path"
                    : "partial order"}
                </i>
              </div>
              <section className="flow-context-grid">
                <div>
                  <span>DATA OBSERVED</span>
                  {selected.dataTypes.map((item) => (
                    <code key={item}>{item}</code>
                  ))}
                </div>
                <div>
                  <span>RESOLVED DESTINATIONS</span>
                  {selected.destinations.length ? (
                    selected.destinations.map((item) => (
                      <code key={item}>{item}</code>
                    ))
                  ) : (
                    <small>No static destination recovered</small>
                  )}
                </div>
              </section>
              {selected.techniques.length > 0 && (
                <section className="flow-attack-map">
                  <span>MITRE ATT&CK MAP</span>
                  <AttackTechniqueChips techniques={selected.techniques} />
                </section>
              )}
              {selected.recoveredValues.length > 0 && (
                <section className="flow-values">
                  <span>RECOVERED VALUES</span>
                  {selected.recoveredValues.map((value) => (
                    <code key={value}>{value}</code>
                  ))}
                </section>
              )}
              <section className="flow-timeline">
                <span>PATH EVIDENCE</span>
                {selected.steps.map((step) => (
                  <div key={`${step.index}-${step.entry}-${step.offset}`}>
                    <i>{step.index}</i>
                    <span>
                      <strong>{step.operation}</strong>
                      <small>{step.detail}</small>
                      <code>
                        {step.className}.{step.method}
                        {step.offset !== undefined ? ` @ ${step.offset}` : ""}
                      </code>
                    </span>
                    <em>{step.role}</em>
                  </div>
                ))}
              </section>
            </>
          ) : (
            <div className="entry-empty">
              <Network size={34} />
              <strong>No data-flow trace selected</strong>
              <span>
                The symbolic evaluator did not find a supported source-to-sink
                path in this archive.
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
